import { Select } from "@theorgchartdev/storybook";
import { BuildInsertOptions } from "@/appComponents/Navigation/ContextualToolbar/InsertMenu/InsertMenu";
import { GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import { useRef } from "react";
import {
  enumBoxSubTypes,
  enumCellContentType,
  kAutoTextStrs,
  enumFileMgrMode,
  enumAutoTypeTypes,
  kDateAutoText,
  kPageNumAutoText,
  kTitleAutoText
} from "@coreapp/constants";
import { QPicDesc } from "@coreapp/CoreApp/QPicDesc";

export const handleImageSelect = (dir, file, handler, area) => {
  const wsMgr = GetWSMgr();
  const doc = GetDoc();

  const callback = {
    ImageLoaded: pRef => {
      const pm = doc?.GetPageMgr();
      const maxWidth = Math.floor(pm?.GetMinPageWidth() * 0.25);
      let maxHeight = area?.includes("bottom") ? pm.GetFooterHeight() : pm.GetHeaderHeight();
      if (maxHeight < 24) maxHeight = 24;

      const dataRef = new QPicDesc();
      dataRef.fDirectory = pRef.fDirectory;
      dataRef.fFileName = pRef.fFileName;
      dataRef.maxHeight = maxHeight;
      dataRef.maxWidth = maxWidth;
      dataRef.scaleImage = true;

      const newLayout = wsMgr.GetNewLayoutByBoxSubType(enumBoxSubTypes.picture, { dataRef });

      handler(newLayout);
    }
  };

  doc.LoadPicture(dir, file, callback, wsMgr?.fCanvasPanel, null, true);
};

export const GetLabelFromLayout = layout => {
  let label = "Select element";
  const type = layout?.GetBoxSubType();
  switch (type) {
    case enumBoxSubTypes.refBoxLegend: // add to this
      label = "Legend";
      break;
    case enumBoxSubTypes.refBoxTable: // add to this
      label = "Table";
      break;
    case enumBoxSubTypes.autoText: // add to this
      label = "Auto text";
      break;
    case enumBoxSubTypes.picture: // add to this
      label = "Picture";
      break;
    case enumBoxSubTypes.chartBox:
      label = "Object";
      return;
    case enumBoxSubTypes.refChart: // add to this
      label = "Pie chart";
      break;
    case enumBoxSubTypes.enumeratedTable: // add to this
      label = "Enumerated table";
      break;
    case enumBoxSubTypes.textBox:
      label = "Static text";
      break;
    default:
      break;
  }
  return label;
};

export const LayoutControls = props => {
  const wsMgr = GetWSMgr();
  const doc = wsMgr?.GetDoc();
  const plm = doc?.GetPageLayoutMgr();
  const scm = plm.GetSubChartMgr();
  const layout = props.value;

  const openCtrlRef = useRef(null);

  const IsAutoText = () => {
    const c1 = layout.GetCellDesc(1, 1);
    const contentType = c1.GetCellContentType();
    return contentType === enumCellContentType.AutoText;
  };

  const GetAutoTextType = () => {
    const c1 = layout.GetCellDesc(1, 1);
    const contentType = c1.GetCellContentType();
    if (contentType === enumCellContentType.AutoText) return kAutoTextStrs[c1.GetAutoTextType()];
    return false;
  };

  const handleUpdate = newLayout => {
    const cLayout = newLayout.Clone({ includeId: true });

    const layoutID = layout.GetDBObjID();
    const layoutDBObj = layout.GetDBObj();

    scm.UpdateLayout(layout, cLayout, layoutDBObj, layoutID);

    props.onChange(layout);
  };

  const handleLayoutTypeChange = o => {
    let options = {};
    // images require user selection
    if (o.value === enumBoxSubTypes.picture) {
      wsMgr.DoFileMgr(enumFileMgrMode.selectimage, {
        customOnSelect: (dir, file) => handleImageSelect(dir, file, handleUpdate, props.area)
      });
      openCtrlRef?.current?.setOpen(false);
      return;
    }
    let newLayout;
    // auto text requires a sub type selection
    const curType = layout.GetBoxSubType();
    let autoTextIndex = kAutoTextStrs.indexOf(o.value);
    if (autoTextIndex > -1) {
      if ([enumBoxSubTypes.autoText].includes(curType)) {
        newLayout = layout.Clone({ includeId: true });
        var c1 = newLayout.GetCellDesc(1, 1);
        c1.SetAutoTextType(autoTextIndex);
        newLayout.SetBoxSubType(enumBoxSubTypes.autoText);
      } else {
        newLayout = wsMgr.GetAutoTextLayout(autoTextIndex);
      }
      // all other layout types
    } else {
      newLayout = wsMgr.GetNewLayoutByBoxSubType(o.value, options);
    }
    handleUpdate(newLayout);
  };

  const targetElement = () => {
    const backgroundMgr = wsMgr.fBackgroundMgr;
    const layoutId = props?.value?.GetDBObjID();
    backgroundMgr.SelectBackgroundElement(layoutId, false);
    ReRenderCanvas();
  };

  let currentLayoutSubType = layout?.GetBoxSubType();
  let layoutTypeValue = [];
  if (IsAutoText()) {
    const autoTextType = GetAutoTextType();
    if (kDateAutoText.includes(autoTextType)) layoutTypeValue = [enumAutoTypeTypes.dates];
    else if (kPageNumAutoText.includes(autoTextType))
      layoutTypeValue = [enumAutoTypeTypes.pagenumbers];
    else if (kTitleAutoText.includes(autoTextType)) layoutTypeValue = [enumAutoTypeTypes.titles];
    layoutTypeValue.push(autoTextType);
  } else {
    if (
      [
        enumBoxSubTypes.refChart,
        enumBoxSubTypes.refBoxTable,
        enumBoxSubTypes.enumeratedTable
      ].includes(currentLayoutSubType)
    )
      layoutTypeValue.push(enumAutoTypeTypes.tables);
    layoutTypeValue.push(currentLayoutSubType);
  }

  return (
    <Select
      dataTestId={props?.dataTestId}
      openCtrlRef={openCtrlRef}
      popoverProps={{
        onOpen: targetElement,
        floatingProps: { "data-testid": "background-elements-insert-popover" }
      }}
      listProps={{
        nestedPopoverProps: {
          floatingProps: { "data-testid": "background-elements-insert-subpopover" }
        }
      }}
      stayOpenOnSelect
      options={BuildInsertOptions({
        clickHandler: handleLayoutTypeChange,
        tagLeafItems: false
      })}
      label={GetLabelFromLayout(layout)}
      value={layoutTypeValue}
      size={"small"}
      optionSize={"small"}
    />
  );
};
